import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  FileText,
  GraduationCap,
  Lightbulb,
  MoreVertical,
  ExternalLink,
  Trash2,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const projectTypes = [
  { value: 'EE', label: 'Extended Essay', icon: FileText, description: '4,000 word research paper' },
  { value: 'IA', label: 'Internal Assessment', icon: GraduationCap, description: 'Subject-specific assessment' },
  { value: 'TOK_ESSAY', label: 'TOK Essay', icon: Lightbulb, description: 'Theory of Knowledge essay' },
  { value: 'TOK_EXHIBITION', label: 'TOK Exhibition', icon: Lightbulb, description: 'TOK exhibition project' },
];

const typeAccent = {
  EE: 'border-l-red-600',
  IA: 'border-l-blue-600',
  TOK_ESSAY: 'border-l-amber-600',
  TOK_EXHIBITION: 'border-l-amber-600',
};

const typeLabel = {
  EE: 'bg-red-50 text-red-700 border-red-200',
  IA: 'bg-blue-50 text-blue-700 border-blue-200',
  TOK_ESSAY: 'bg-amber-50 text-amber-700 border-amber-200',
  TOK_EXHIBITION: 'bg-amber-50 text-amber-700 border-amber-200',
};

const statusLabels = {
  PLANNING: 'Planning',
  RESEARCH: 'Research',
  DRAFTING: 'Drafting',
  REVIEWING: 'Reviewing',
  FINAL: 'Final',
};

const ProjectsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    type: '',
    subject: '',
    courseLevel: '',
    title: '',
    researchQuestion: '',
    googleDocsUrl: '',
    notes: ''
  });

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await api.get('/projects');
      setProjects(res.data.projects || []);
    } catch (err) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!formData.type) {
      toast.error('Please select a project type');
      return;
    }
    try {
      const res = await api.post('/projects', formData);
      setProjects([res.data.project, ...projects]);
      setDialogOpen(false);
      setFormData({ type: '', subject: '', courseLevel: '', title: '', researchQuestion: '', googleDocsUrl: '', notes: '' });
      toast.success('Project created!');
      navigate(`/projects/${res.data.project.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create project');
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects(projects.filter(p => p.id !== id));
      toast.success('Project deleted');
    } catch (err) {
      toast.error('Failed to delete project');
    }
  };

  const groupedProjects = {
    EE: projects.filter(p => p.type === 'EE'),
    IA: projects.filter(p => p.type === 'IA'),
    TOK: projects.filter(p => p.type === 'TOK_ESSAY' || p.type === 'TOK_EXHIBITION'),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm text-muted-foreground font-mono animate-pulse">Loading projects...</div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto animate-fade-in" data-testid="projects-page">
      {/* Header */}
      <div className="flex items-start justify-between border-b border-border pb-5 mb-6">
        <div>
          <p className="label-swiss mb-1">IB Coursework</p>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Projects</h1>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="create-project-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create New Project</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateProject} className="space-y-4">
              <div className="space-y-2">
                <Label className="label-swiss">Project Type *</Label>
                <div className="grid grid-cols-2 gap-2">
                  {projectTypes.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, type: type.value })}
                      className={`p-3 border text-left transition-colors ${
                        formData.type === type.value
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-foreground/30'
                      }`}
                      data-testid={`project-type-${type.value}`}
                    >
                      <type.icon className={`w-4 h-4 mb-1.5 ${formData.type === type.value ? 'text-primary' : 'text-muted-foreground'}`} />
                      <p className="font-semibold text-sm">{type.label}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{type.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="subject" className="text-xs font-medium">Subject</Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="e.g., Biology, History"
                    data-testid="project-subject-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="courseLevel" className="text-xs font-medium">Level</Label>
                  <Select
                    value={formData.courseLevel}
                    onValueChange={(v) => setFormData({ ...formData, courseLevel: v })}
                  >
                    <SelectTrigger data-testid="project-level-select">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HL">Higher Level (HL)</SelectItem>
                      <SelectItem value="SL">Standard Level (SL)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="title" className="text-xs font-medium">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Project title"
                  data-testid="project-title-input"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="researchQuestion" className="text-xs font-medium">Research Question</Label>
                <Textarea
                  id="researchQuestion"
                  value={formData.researchQuestion}
                  onChange={(e) => setFormData({ ...formData, researchQuestion: e.target.value })}
                  placeholder="Your research question..."
                  rows={2}
                  data-testid="project-rq-input"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="googleDocsUrl" className="text-xs font-medium">Google Docs Link</Label>
                <Input
                  id="googleDocsUrl"
                  type="url"
                  value={formData.googleDocsUrl}
                  onChange={(e) => setFormData({ ...formData, googleDocsUrl: e.target.value })}
                  placeholder="https://docs.google.com/..."
                  data-testid="project-docs-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <Button type="button" variant="outline" size="sm" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" data-testid="project-submit-btn">
                  Create Project
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {projects.length === 0 ? (
        <div className="border border-border py-16 text-center">
          <FileText className="w-8 h-8 mx-auto mb-3 text-muted-foreground/40" />
          <h3 className="font-semibold text-foreground mb-1">No projects yet</h3>
          <p className="text-sm text-muted-foreground mb-5">Create your first project to start organizing your IB work</p>
          <Button size="sm" onClick={() => setDialogOpen(true)}>
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Create Your First Project
          </Button>
        </div>
      ) : (
        <div className="space-y-8">
          {groupedProjects.EE.length > 0 && (
            <ProjectSection
              title="Extended Essay"
              colorDot="bg-red-600"
              projects={groupedProjects.EE}
              onDelete={handleDeleteProject}
            />
          )}
          {groupedProjects.IA.length > 0 && (
            <ProjectSection
              title="Internal Assessments"
              colorDot="bg-blue-600"
              projects={groupedProjects.IA}
              onDelete={handleDeleteProject}
            />
          )}
          {groupedProjects.TOK.length > 0 && (
            <ProjectSection
              title="Theory of Knowledge"
              colorDot="bg-amber-600"
              projects={groupedProjects.TOK}
              onDelete={handleDeleteProject}
            />
          )}
        </div>
      )}
    </div>
  );
};

const ProjectSection = ({ title, colorDot, projects, onDelete }) => (
  <section>
    <div className="flex items-center gap-2 border-b border-border pb-2 mb-0">
      <div className={`w-2 h-2 ${colorDot} shrink-0`} />
      <h2 className="text-sm font-semibold uppercase tracking-wide text-foreground">{title}</h2>
      <span className="font-mono text-xs text-muted-foreground ml-auto">{projects.length}</span>
    </div>
    <div className="border border-t-0 border-border divide-y divide-border">
      {projects.map((project) => (
        <ProjectRow key={project.id} project={project} onDelete={onDelete} />
      ))}
    </div>
  </section>
);

const ProjectRow = ({ project, onDelete }) => {
  const completedItems = project.checklistItems?.filter(i => i.completed).length || 0;
  const totalItems = project.checklistItems?.length || 0;
  const progress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  return (
    <div className={`flex items-center gap-4 px-4 py-3 hover:bg-muted/30 transition-colors group border-l-2 ${typeAccent[project.type]}`}>
      <Link to={`/projects/${project.id}`} className="flex-1 min-w-0 flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-sm font-medium text-foreground group-hover:text-primary transition-colors truncate">
              {project.title || project.subject || 'Untitled Project'}
            </h3>
            {project.courseLevel && (
              <span className="text-[10px] font-mono text-muted-foreground shrink-0">{project.courseLevel}</span>
            )}
          </div>
          {project.subject && project.title && (
            <p className="text-[11px] text-muted-foreground">{project.subject}</p>
          )}
        </div>

        <div className="flex items-center gap-4 shrink-0">
          <span className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
            {statusLabels[project.status]}
          </span>
          {totalItems > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-20 h-1 bg-muted">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-[10px] font-mono text-muted-foreground">{completedItems}/{totalItems}</span>
            </div>
          )}
          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
        </div>
      </Link>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            <MoreVertical className="w-3.5 h-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {project.googleDocsUrl && (
            <DropdownMenuItem onClick={() => window.open(project.googleDocsUrl, '_blank')}>
              <ExternalLink className="w-3.5 h-3.5 mr-2" />
              Open in Google Docs
            </DropdownMenuItem>
          )}
          <DropdownMenuItem className="text-destructive" onClick={() => onDelete(project.id)}>
            <Trash2 className="w-3.5 h-3.5 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ProjectsPage;
